<template>
  <section v-editable="blok" class="relative h-full">
    <Intersect :threshold="0.5" @enter.once="handleVisibility">
      <nuxt-link
        :to="url"
        :class="{
          'flex h-full flex-col bg-gold md:flex-row': leftAligned,
        }"
        @click="clickObserver"
      >
        <HeroImage
          :source="backgroundImage"
          :cover="!leftAligned"
          :lazy="lazy"
          :high-priority="!lazy"
          :alt="altText"
          :class="{
            '!h-auto !w-auto basis-1/2 md:order-1 md:shrink-0': leftAligned,
          }"
          @load="$emit('load')"
        />
        <div
          class="flex size-full flex-col flex-nowrap items-center justify-center text-center"
          :class="{
            'absolute top-0': defaultLayout || bottomLeftAligned,
            'flex-1 p-4': leftAligned,
            '!items-start !justify-end p-8 !text-left md:px-16 md:py-24 xl:w-3/5':
              bottomLeftAligned,
          }"
        >
          <div
            v-if="blok.headline && blok.kicker"
            :class="{ container: bottomLeftAligned }"
            class="w-full"
          >
            <Responsive
              :base-width="500"
              :initial-height="leftAligned ? 300 : 500"
              :align="bottomLeftAligned ? 'bottom' : 'center'"
            >
              <h2
                v-if="blok.kicker"
                class="hero-banner__kicker mb-6 max-w-full break-words text-xl font-bold uppercase tracking-widest md:mb-7"
                :class="{
                  'text-black': blok.textColor === 'black',
                  'text-white': blok.textColor !== 'black',
                }"
              >
                {{ blok.kicker }}
              </h2>
              <h2
                v-if="blok.headline"
                class="hero-banner__headline max-w-full break-words leading-[5rem] md:leading-[9rem]"
                :class="{
                  'text-black': blok.textColor === 'black',
                  'text-white': blok.textColor !== 'black',
                  'mb-6 font-handwritten text-banner md:mb-7 md:text-bannerLg':
                    !blok.isRegularFont,
                  'mb-10 text-4xl md:mb-12 md:text-banner': blok.isRegularFont,
                  '!leading-[4rem] md:!leading-[6rem]': bottomLeftAligned,
                }"
              >
                {{ blok.headline }}
              </h2>
            </Responsive>
            <button
              v-if="blok.buttonText"
              class="hero-banner__btn bg-white px-3.5 py-2 text-sm leading-4 tracking-widest shadow-bannerButton transition hover:bg-gray md:px-5 md:py-3.5 md:text-base md:leading-[19px]"
              type="button"
              :class="{ 'opacity-0': isMobile && !mounted }"
            >
              {{ blok.buttonText }}
            </button>

            <Responsive
              v-if="blok.campaignText"
              :base-width="500"
              :initial-height="50"
              align="center"
              :class="{ 'opacity-0': isMobile && !mounted }"
            >
              <div
                class="campaign-text--thick mt-10 text-4xl font-bold uppercase leading-tight md:mt-14 md:text-banner"
                :class="`text-${blok.campaignTextColor || 'gold'}`"
              >
                {{ blok.campaignText }}
              </div>
            </Responsive>
          </div>
        </div>
      </nuxt-link>
    </Intersect>
  </section>
</template>

<script lang="ts">
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { defaultSizes } from '~/composables/useSbImage'
import { useDeviceType } from '~/composables/useDeviceType'
import HeroImage from '~/components/images/HeroImage.vue'
import Responsive from '#layers/base/components/utils/Responsive.vue'
import type { SbHeroBanner } from '~/cms/storyblok/types/storyblok.d.ts'

// Include tailwind classes:
// text-gold-10
// text-gold-20
// text-gold-40
// text-gold-60
// text-gold-80

export default defineComponent({
  name: 'HeroBanner',
  components: {
    HeroImage,
    Responsive,
  },
  props: {
    blok: {
      type: Object as PropType<SbHeroBanner>,
      default: () => {},
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    index: {
      type: String,
      default: '1',
    },
  },
  emits: ['load'],
  setup({ blok, index }) {
    const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
    const { toLocalePath } = useRouteHelpers()
    const { isMobile } = useDeviceType()
    const mounted = ref(false)

    const backgroundImage = computed<string>(() => {
      if (isMobile.value && blok.backgroundMobile?.filename) {
        return blok.backgroundMobile.filename
      } else {
        return blok.background.filename
      }
    })

    const altText = computed<string>(() => {
      if (
        isMobile.value &&
        blok.backgroundMobile?.filename &&
        blok.backgroundMobileAlt
      ) {
        return blok.backgroundMobileAlt
      } else {
        return blok.backgroundAlt || ''
      }
    })

    const fetching = ref(true)

    const url = computed(() =>
      toLocalePath(blok?.url?.cached_url || '').toString(),
    )

    onMounted(() => (mounted.value = true))

    return {
      url,
      backgroundImage,
      fetching,
      defaultSizes,
      mounted,
      isMobile,
      defaultLayout: !blok.layout || blok.layout === 'default',
      leftAligned: blok.layout === 'left',
      bottomLeftAligned: blok.layout === 'bottom-left',
      altText,
      clickObserver: () => {
        trackPromotionSelect({
          id: blok.promotion_id || 'hero_banner_promotion_id',
          name: blok.promotion_name || 'hero_banner_promotion_name',
          promoType: 'herobanner',
          position: Number(index) + 1,
          creativeNameSb: blok.creative_name || '',
        })
      },
      handleVisibility: () =>
        trackPromotionView({
          id: blok.promotion_id || 'hero_banner_promotion_id',
          name: blok.promotion_name || 'hero_banner_promotion_name',
          promoType: 'herobanner',
          position: Number(index) + 1,
          creativeNameSb: blok.creative_name || '',
        }),
    }
  },
})
</script>

<style lang="scss" scoped>
.hero-banner__kicker {
  text-shadow: 0 4px 40px rgba(0, 0, 0, 0.4);
}

.hero-banner__headline {
  text-shadow: 0 4px 40px rgba(0, 0, 0, 0.4);
}

.hero-banner__btn {
  box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.45);
}

.campaign-text--thick {
  text-shadow:
    0 1px,
    1px 0,
    1px 1px;
}
</style>
